import { motion } from "framer-motion";
import { SafeImage } from "@/components/ui/SafeImage";

interface LandingLogoProps {
  siteName?: string;
  headline?: string | null;
  subheadline?: string | null;
  logoUrl?: string | null;
  isLoading?: boolean;
}

export function LandingLogo({ siteName, headline, subheadline, logoUrl, isLoading }: LandingLogoProps) {
  const displayName = headline || siteName || "Kalaco";
  const displaySubheadline = subheadline || "Liveries";

  return (
    <motion.div
      className="relative flex w-full justify-center"
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
    >
      <LogoBacklight />
      <div className="relative z-10">
        {isLoading ? (
          <div className="h-40 w-[min(500px,84vw)] animate-pulse rounded-md bg-white/10 sm:h-64" />
        ) : logoUrl ? (
          <SafeImage
            src={logoUrl}
            alt={`${siteName || "Kalaco's Liveries"} logo`}
            fallbackLabel="Kalaco"
            className="h-auto w-[min(500px,84vw)] max-w-full object-contain drop-shadow-[0_0_26px_rgba(34,211,238,0.22)] sm:w-[min(460px,82vw)] lg:w-[500px]"
          />
        ) : (
          <div className="flex w-full max-w-[500px] flex-col items-center text-center">
            <div className="font-display text-[5rem] font-black italic leading-none tracking-[-0.08em] text-white sm:text-[10rem]">
              KL
            </div>
            <div className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.2em] text-white sm:text-6xl sm:tracking-[0.38em]">
              {displayName}
            </div>
            <div className="mt-3 border-y border-cyan-300 px-5 py-1 font-display text-base uppercase tracking-[0.28em] text-cyan-300 sm:px-10 sm:text-2xl sm:tracking-[0.6em]">
              {displaySubheadline}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function LogoBacklight() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-1/2 z-0 h-[24rem] w-[44rem] max-w-[92vw] -translate-x-1/2 -translate-y-1/2">
      <motion.div
        className="absolute inset-12 rounded-full bg-cyan-300/10 blur-3xl"
        animate={{ opacity: [0.24, 0.5, 0.28, 0.62, 0.24], scale: [0.96, 1.04, 1] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute left-1/2 top-1/2 h-px w-[82%] -translate-x-1/2 -translate-y-1/2 rotate-[-8deg] bg-gradient-to-r from-transparent via-cyan-200 to-transparent shadow-[0_0_28px_rgba(34,211,238,0.85)]"
        animate={{ opacity: [0, 0.75, 0.08, 0.55, 0], scaleX: [0.3, 1, 0.82, 1, 0.4] }}
        transition={{ duration: 3.8, repeat: Infinity, repeatDelay: 1.4, ease: "easeOut" }}
      />
      <motion.div
        className="absolute left-[18%] top-[42%] h-1 w-[38%] rotate-[-26deg] bg-gradient-to-r from-transparent via-teal-300 to-cyan-100 shadow-[0_0_18px_rgba(45,212,191,0.9)]"
        animate={{ opacity: [0, 0.88, 0.12, 0], x: [-10, 0, 8, 14] }}
        transition={{ duration: 2.9, repeat: Infinity, repeatDelay: 2.1, ease: "easeOut" }}
      />
      <motion.div
        className="absolute right-[16%] top-[50%] h-1 w-[34%] rotate-[22deg] bg-gradient-to-r from-cyan-100 via-cyan-300 to-transparent shadow-[0_0_18px_rgba(34,211,238,0.9)]"
        animate={{ opacity: [0, 0.72, 0.18, 0.66, 0], x: [10, 0, -6, -12] }}
        transition={{ duration: 3.2, repeat: Infinity, repeatDelay: 1.8, ease: "easeOut" }}
      />
      <motion.div
        className="absolute left-[30%] top-[30%] h-px w-[48%] rotate-[8deg] bg-gradient-to-r from-transparent via-white to-transparent opacity-60 shadow-[0_0_16px_rgba(255,255,255,0.65)]"
        animate={{ opacity: [0, 0.5, 0, 0.34, 0], scaleX: [0.1, 1, 0.45, 0.9, 0.2] }}
        transition={{ duration: 4.6, repeat: Infinity, repeatDelay: 0.8, ease: "easeInOut" }}
      />
    </div>
  );
}
